import { STORAGE_KEYS } from '../config/storage-keys';
import { logger } from './logger';

/**
 * Migration utilities for localStorage keys
 * Used to migrate from old key names to new centralized keys
 */

interface Migration {
  oldKey: string;
  newKey: string;
  description: string;
}

const MIGRATIONS: Migration[] = [
  {
    oldKey: 'activeEsimId',
    newKey: STORAGE_KEYS.ACTIVE_ESIM_ID,
    description: 'Active eSIM ID',
  },
  {
    oldKey: 'lastUsedEsimByWallet',
    newKey: STORAGE_KEYS.LAST_USED_ESIM_BY_WALLET,
    description: 'Last used eSIM per wallet',
  },
];

/**
 * Migrate old localStorage keys to new prefixed keys
 * This is idempotent - can be called multiple times safely
 */
export function migrateStorageKeys(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  logger.log('[StorageMigration] Starting storage key migration...');
  let migratedCount = 0;

  MIGRATIONS.forEach(({ oldKey, newKey, description }) => {
    try {
      const oldValue = localStorage.getItem(oldKey);
      if (oldValue !== null) {
        const newValue = localStorage.getItem(newKey);
        if (newValue === null) {
          localStorage.setItem(newKey, oldValue);
          logger.log(`[StorageMigration] Migrated: ${description} (${oldKey} → ${newKey})`);
          migratedCount++;
        }
        
        localStorage.removeItem(oldKey);
        logger.log(`[StorageMigration] Removed old key: ${oldKey}`);
      }
    } catch (error) {
      logger.warn(`[StorageMigration] Failed to migrate ${oldKey}:`, error);
    }
  });

  if (migratedCount > 0) {
    logger.log(`[StorageMigration] Migration complete. Migrated ${migratedCount} key(s).`);
  } else {
    logger.log('[StorageMigration] No keys to migrate.');
  }
}

/**
 * Check if there are any old keys that need migration
 */
export function needsStorageMigration(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  return MIGRATIONS.some(({ oldKey }) => {
    try {
      return localStorage.getItem(oldKey) !== null;
    } catch {
      return false;
    }
  });
}

